import type { Provenance, Question, SourceFile } from "@/braining/types";

/**
 * Заготовки для демонстрации.
 *
 * Здесь то, что в рабочей системе придёт от AI: разбор материалов, пакет
 * уточнений, реплики ассистента. Пока это фикстуры под один сценарий —
 * «Бренд работодателя». Меняются на серверные функции без правки экранов.
 */

/** Материалы проекта, как их складывает Аня: брифы, встречи, чаты, расчёты. */
export const DEMO_SOURCES: SourceFile[] = [
  {
    id: "s1",
    name: "Бриф на бренд работодателя.docx",
    kind: "docx",
    sizeBytes: 248_000,
    date: "2026-08-04",
    extracted: "клиент, задача, состав аудиторий, требование карты конкурентов",
    // Ровно случай, который описывала Аня: в брифе ссылка на Figma, где лежат
    // материалы для расчёта, а модель туда зайти не может и начинает выдумывать.
    unreachableLinks: ["https://figma.com/file/…/HR-brand-materials"],
  },
  {
    id: "s2",
    name: "Установочная встреча 04.08 — расшифровка.txt",
    kind: "transcript",
    sizeBytes: 96_000,
    date: "2026-08-04",
    extracted: "тип проекта, формат стратсессии, ожидания по срокам",
  },
  {
    id: "s3",
    name: "Переписка с HR-командой (выгрузка).txt",
    kind: "chat",
    sizeBytes: 41_000,
    date: "2026-08-11",
    extracted: "срок передачи сметы, контактные лица",
  },
  {
    id: "s4",
    name: "Отчёт по рынку труда 2026.pdf",
    kind: "pdf",
    sizeBytes: 18_400_000,
    date: "2026-06-01",
    extracted: "ничего существенного для расчёта",
  },
  {
    id: "s5",
    name: "Расчёт подрядчика — внешняя панель.xlsx",
    kind: "xlsx",
    sizeBytes: 32_000,
    date: "2026-08-12",
    extracted: "расчёт подрядчика на внешнюю панель, 536 000 ₽",
  },
];

/**
 * Что предлагается отдать подрядчику.
 *
 * Привязка идёт к строке, а не к блоку: в «Количественном исследовании внешнем»
 * три строки — наши часы по рейткарте, и только «Внешняя панель» уходит на
 * сторону. Подтягивается на шаге «Расчёт», когда у блоков появились услуги.
 */
export const DEMO_CONTRACTOR: {
  lineName: string;
  supplier: string;
  sourceId: string;
  cost: number;
} = {
  lineName: "Внешняя панель (до 1000 респондентов)",
  supplier: "Анкетолог",
  sourceId: "s5",
  cost: 536_000,
};

/**
 * Откуда взялось каждое вводное.
 *
 * Первый экран показывает не форму, а разобранное — и у каждой карточки видно
 * источник. Неизвестное не становится пустым полем: у него статус «unknown»,
 * и оно само уезжает в уточнения.
 */
export const DEMO_PROVENANCE: Record<string, Provenance> = {
  client: { sourceId: "s1", note: "шапка брифа", status: "confirmed" },
  title: { sourceId: "s1", note: "тема брифа", status: "confirmed" },
  projectType: {
    sourceId: "s2",
    note: "встреча 04.08: «нужна платформа бренда работодателя»",
    status: "confirmed",
  },
  division: { note: "выведено по типу проекта", status: "assumed" },
  deadline: { sourceId: "s3", note: "переписка 11.08: «смету ждём к 25-му»", status: "confirmed" },
  // Целевая рентабельность берётся из справочника: у Comm план 70%.
  targetMargin: { note: "справочник: план подразделения Comm", status: "confirmed" },
  // Бюджета в документах не бывает — он живёт в голове у менеджера после
  // разговоров с клиентом. Спрашиваем прямо, а не делаем вид, что нашли.
  budget: { note: "со слов менеджера, в документах не встречается", status: "assumed" },
  taxSchemeId: { note: "юрлицо не обсуждалось", status: "unknown" },
};

/**
 * Пакет уточнений демонстрационной сметы.
 *
 * По ТЗ спрашивать можно только то, что меняет состав работ, объём, цену,
 * рентабельность, сроки или права. Два вопроса здесь действительно меняют часы.
 *
 * В рабочем пути уточнения приходят от разбора материалов (`analysis.ts`) — с
 * теми же правилами и без выдуманных часов. Здесь они остались фикстурой
 * демо-сметы: показывать пустой экран уточнений на встрече незачем.
 */
export const DEMO_QUESTIONS: Question[] = [
  {
    id: "q1",
    text: "Сколько конкурентов берём в коммуникационный анализ?",
    affects: "объём работ аналитика в блоке конкурентного анализа",
    options: [
      {
        id: "q1a",
        label: "До 10 компаний",
        recommended: true,
        effect: {
          libraryId: "konkurentnyy_kommunikacionnyy_analiz",
          role: "Аналитик",
          qty: 45,
        },
      },
      {
        id: "q1b",
        label: "До 20 компаний",
        effect: {
          libraryId: "konkurentnyy_kommunikacionnyy_analiz",
          role: "Аналитик",
          qty: 80,
        },
      },
    ],
  },
  {
    id: "q2",
    text: "Количественное исследование внешнее — закупаем панель?",
    affects: "цену блока: панель до 1000 респондентов стоит 750 000 ₽",
    options: [
      {
        id: "q2a",
        label: "Да, внешняя панель до 1000 респондентов",
        recommended: true,
        effect: {
          libraryId: "kolichestvennoe_issledovanie_vneshnee",
          role: "Внешняя панель (до 1000 респондентов)",
          qty: 1,
        },
      },
      {
        id: "q2b",
        label: "Нет, поле проводит клиент своими силами",
        effect: {
          libraryId: "kolichestvennoe_issledovanie_vneshnee",
          role: "Внешняя панель (до 1000 респондентов)",
          qty: 0,
        },
      },
    ],
  },
  {
    id: "q3",
    text: "Стратегическая сессия — офлайн или онлайн?",
    affects: "командировочные и продолжительность сессии",
    options: [
      { id: "q3a", label: "Офлайн, до 12 участников", recommended: true },
      { id: "q3b", label: "Онлайн" },
    ],
  },
  {
    id: "q4",
    text: "Сколько итераций правок закладываем в каждый блок?",
    affects: "объём работ и текст оговорки в клиентской версии",
    options: [
      { id: "q4a", label: "3 итерации, как в шаблоне", recommended: true },
      { id: "q4b", label: "2 итерации" },
      { id: "q4c", label: "Без ограничения — осмечиваем по факту" },
    ],
  },
  {
    id: "q5",
    text: "Фотосъёмка для визуальной концепции входит в смету?",
    affects: "состав работ: съёмка считается отдельной опцией",
    options: [
      { id: "q5a", label: "Нет, выносим отдельной опцией", recommended: true },
      { id: "q5b", label: "Да, включаем в основной расчёт" },
    ],
  },
  {
    id: "q6",
    text: "Права на использование материалов",
    affects: "стоимость креативной части и текст договора",
    options: [
      { id: "q6a", label: "Россия, 1 год, digital", recommended: true },
      { id: "q6b", label: "Россия, бессрочно, все каналы" },
    ],
  },
];
